type Listener = (...args: unknown[]) => void;

class EventBus {
	private listeners = new Map<string, Set<Listener>>();

	on(event: string, listener: Listener): () => void {
		if (!this.listeners.has(event)) {
			this.listeners.set(event, new Set());
		}
		this.listeners.get(event)!.add(listener);
		return () => this.off(event, listener);
	}

	off(event: string, listener: Listener): void {
		this.listeners.get(event)?.delete(listener);
	}

	emit(event: string, ...args: unknown[]): void {
		this.listeners.get(event)?.forEach((listener) => listener(...args));
	}
}

export const authEventBus = new EventBus();

// Carries failed API calls (network/server errors) to a global toast host, so a silent fetch failure becomes
// visible feedback instead of just stale cached data.
export const apiEventBus = new EventBus();

export interface ApiErrorEvent {
	// 'network' = no HTTP response (offline, timeout, unreachable server); 'server' = a response with a non-2xx
	// status (5xx, or 4xx other than 401).
	kind: 'network' | 'server';
	status?: number;
}

// Carries a notice raised outside the React tree to the dialog host on web. errorHandler lives in
// core, which cannot import a domain store (the layer rule forbids it), so the request travels as
// an event and useAlertDialogStore puts it in its queue.
export const alertEventBus = new EventBus();

/** One entry of an explicit button list; mirrors ShowAlertButton without importing react-native. */
export interface AlertRequestButton {
	text: string;
	isCancel?: boolean;
	isDestructive?: boolean;
	onPress?: () => void;
}

export interface AlertRequestEvent {
	title: string;
	// null means "title only", which is how errorHandler is called for a message that needs no
	// second line (Alert.alert takes undefined there).
	message: string | null;
	// Everything below is optional because errorHandler raises a plain notice and sets none of it.
	// showAlert fills them in for an acknowledgement that runs a callback, and for the two-button
	// question raised outside presentation, which cannot import AlertHelper.
	kind?: 'notice' | 'confirm';
	confirmText?: string;
	cancelText?: string;
	isDestructive?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	// Set instead of the four fields above when a question has more than two answers, or two that
	// both do something. The dialog then draws exactly these buttons, in this order.
	buttons?: AlertRequestButton[];
}
